import React, { useState, useCallback, useRef } from 'react';
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  GripVertical,
  Type,
  Image as ImageIcon,
  Square,
  Circle,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import type { SocialCanvasElement, SocialCanvasElementType } from '../../../services/socialPosting';

const TYPE_ICONS: Record<SocialCanvasElementType, React.ElementType> = {
  text: Type,
  image: ImageIcon,
  rect: Square,
  circle: Circle,
};

const TYPE_LABELS: Record<SocialCanvasElementType, string> = {
  text: 'Text',
  image: 'Image',
  rect: 'Rectangle',
  circle: 'Circle',
};

interface LayersPanelProps {
  elements: SocialCanvasElement[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onUpdateElement: (index: number, updates: Partial<SocialCanvasElement>) => void;
  onMoveLayer: (index: number, direction: 'up' | 'down') => void;
  onReorderLayer: (fromIndex: number, toIndex: number) => void;
}

export function LayersPanel({
  elements,
  selectedIndex,
  onSelect,
  onUpdateElement,
  onMoveLayer,
  onReorderLayer,
}: LayersPanelProps) {
  // Display in reverse z-order (top layer first in the list)
  const reversed = [...elements].map((el, i) => ({ el, originalIndex: i })).reverse();

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<number | null>(null);
  const dragNodeRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, originalIndex: number) => {
    dragNodeRef.current = originalIndex;
    setDragIndex(originalIndex);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    if (e.currentTarget instanceof HTMLElement) {
      e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, originalIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragNodeRef.current !== null && dragNodeRef.current !== originalIndex) {
      setDropTarget(originalIndex);
    }
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetOriginalIndex: number) => {
    e.preventDefault();
    if (dragNodeRef.current !== null && dragNodeRef.current !== targetOriginalIndex) {
      onReorderLayer(dragNodeRef.current, targetOriginalIndex);
    }
    setDragIndex(null);
    setDropTarget(null);
    dragNodeRef.current = null;
  }, [onReorderLayer]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDropTarget(null);
    dragNodeRef.current = null;
  }, []);

  return (
    <div className="w-[200px] shrink-0 border-r bg-background flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b bg-muted/30">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Layers</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {reversed.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <p className="text-xs text-muted-foreground">No elements yet</p>
          </div>
        ) : (
          <div className="py-1">
            {reversed.map(({ el, originalIndex }) => {
              const Icon = TYPE_ICONS[el.type];
              const isSelected = selectedIndex === originalIndex;
              const isVisible = el.visible !== false;
              const isLocked = el.locked === true;
              const name = el.name || TYPE_LABELS[el.type];
              const isDragging = dragIndex === originalIndex;
              const isDropTarget = dropTarget === originalIndex;

              return (
                <div
                  key={el.id || originalIndex}
                  draggable
                  onDragStart={(e) => handleDragStart(e, originalIndex)}
                  onDragOver={(e) => handleDragOver(e, originalIndex)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, originalIndex)}
                  onDragEnd={handleDragEnd}
                  onClick={() => onSelect(originalIndex)}
                  className={`
                    group flex items-center gap-1.5 px-2 py-1.5 mx-1 rounded-lg cursor-pointer text-sm transition-colors
                    ${isSelected
                      ? 'bg-primary/10 text-foreground border border-primary/30'
                      : 'hover:bg-muted/50 text-muted-foreground border border-transparent'
                    }
                    ${!isVisible ? 'opacity-50' : ''}
                    ${isDragging ? 'opacity-30' : ''}
                    ${isDropTarget ? 'border-primary border-dashed !border-2' : ''}
                  `}
                >
                  {/* Drag handle */}
                  <GripVertical className="h-3 w-3 shrink-0 opacity-30 cursor-grab active:cursor-grabbing" />

                  {/* Type icon */}
                  <Icon className="h-3.5 w-3.5 shrink-0" />

                  {/* Name */}
                  <span className="flex-1 truncate text-xs">{name}</span>

                  {/* Move up/down */}
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveLayer(originalIndex, 'up'); }}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Move up"
                  >
                    <ChevronUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onMoveLayer(originalIndex, 'down'); }}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Move down"
                  >
                    <ChevronDown className="h-3 w-3" />
                  </button>

                  {/* Visibility toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateElement(originalIndex, { visible: !isVisible } as Partial<SocialCanvasElement>);
                    }}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80"
                    title={isVisible ? 'Hide' : 'Show'}
                  >
                    {isVisible ? (
                      <Eye className="h-3 w-3" />
                    ) : (
                      <EyeOff className="h-3 w-3" />
                    )}
                  </button>

                  {/* Lock toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpdateElement(originalIndex, { locked: !isLocked } as Partial<SocialCanvasElement>);
                    }}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted/80"
                    title={isLocked ? 'Unlock' : 'Lock'}
                  >
                    {isLocked ? (
                      <Lock className="h-3 w-3 text-primary" />
                    ) : (
                      <Unlock className="h-3 w-3 opacity-30" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="px-3 py-2 border-t bg-muted/20">
        <p className="text-[10px] text-muted-foreground text-center">
          {elements.length} element{elements.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
}
